import { Avatar, Box, Button, Card, CardActions, CardContent, CardHeader, Divider, IconButton, Typography } from "@mui/material";
import { Facebook, Public, MoreHoriz, ThumbUpOffAlt, ChatBubbleOutlineOutlined, ShareOutlined } from "@mui/icons-material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { getMediaKind } from "../util";

type Placement = "timeline" | "reels" | "stories";

interface Props {
  caption: string;
  placement?: Placement;
  media?: string[];
  mediaKinds?: Record<string, "image" | "video">;
}

export default function FacebookPostPreview({ caption, placement = "timeline", media = [], mediaKinds }: Props) {
  const trimmed = caption?.trim();
  const showCaption = placement === "timeline" && !!trimmed;
  const showPlaceholder = placement === "timeline" && !trimmed;
  const showMedia = placement === "timeline" && media.length > 0;

  return (
    <Card variant="outlined" sx={styles.card}>
      <CardHeader
        sx={styles.header}
        avatar={
          <Avatar sx={styles.logoAvatar}>
            <Facebook fontSize="small" />
          </Avatar>
        }
        disableTypography
        title={
          <Box sx={styles.titleWrap}>
            <Typography sx={styles.pageName}>Your Page</Typography>
            <Box sx={styles.sublineRow}>
              <Typography sx={styles.sublineText}>Just now</Typography>
              <Typography sx={styles.dotSeparator}>·</Typography>
              <Public sx={styles.sublineIcon} />
              <Typography sx={styles.sublineText}>Public</Typography>
            </Box>
          </Box>
        }
        action={
          <IconButton size="small" sx={styles.moreBtn}>
            <MoreHoriz />
          </IconButton>
        }
      />

      {(showCaption || showPlaceholder) && (
        <CardContent sx={styles.cardContent}>
          <Typography sx={styles.captionText}>
            {showCaption ? trimmed : "Write something to post..."}
          </Typography>
        </CardContent>
      )}

      {showMedia && (
        <Box sx={styles.mediaWrap}>
          <FacebookMediaCollage urls={media} mediaKinds={mediaKinds} />
        </Box>
      )}

      <Divider sx={styles.divider} />

      <CardActions sx={styles.actions}>
        <Button sx={styles.actionBtn} startIcon={<ThumbUpOffAlt sx={styles.actionIcon} />}>Like</Button>
        <Button sx={styles.actionBtn} startIcon={<ChatBubbleOutlineOutlined sx={styles.actionIcon} />}>Comment</Button>
        <Button sx={styles.actionBtn} startIcon={<ShareOutlined sx={styles.actionIcon} />}>Share</Button>
      </CardActions>
    </Card>
  );
}

function FacebookMediaCollage({ urls, mediaKinds }: { urls: string[]; mediaKinds?: Record<string, "image" | "video"> }) {
  const count = urls.length;
  const shown = count <= 5 ? urls : urls.slice(0, 5);
  const extra = Math.max(0, count - shown.length);

  const isVideo = (u: string) =>
    mediaKinds?.[u] === "video" ||
    getMediaKind(u) === "video" ||
    /\.(mp4|mov|avi|mkv|webm)(\?|#|$)/i.test(u);

  const layouts = {
    1: {
      sx: { display: "block" },
      tiles: [{ sx: { height: 420 } }] 
    },
    2: {
      sx: { display: "grid", gridTemplateColumns: "1fr 1fr", gridAutoRows: "220px", gap: "4px" },
      tiles: [] 
    },
    3: {
      sx: { display: "grid", gridTemplateColumns: "2fr 1fr", gridTemplateRows: "repeat(2, 160px)", gap: "4px" },
      tiles: [
        { sx: { gridRow: "1 / span 2", height: 322 } },
        { sx: { height: 160 } },
        { sx: { height: 160 } }
      ]
    },
    4: {
      sx: { display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "repeat(2, 180px)", gap: "4px" },
      tiles: [] 
    }
  };

  if (count >= 5) {
    const overlayText = extra > 0 ? `+${extra}` : undefined;
    return (
      <Box sx={{ display: "grid", gridTemplateRows: "200px 120px", gap: "4px" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px" }}>
          <MediaTile key={shown[0]} url={shown[0]} isVideo={isVideo(shown[0])} />
          <MediaTile key={shown[1]} url={shown[1]} isVideo={isVideo(shown[1])} />
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "4px" }}>
          <MediaTile key={shown[2]} url={shown[2]} isVideo={isVideo(shown[2])} />
          <MediaTile key={shown[3]} url={shown[3]} isVideo={isVideo(shown[3])} />
          <MediaTile key={shown[4]} url={shown[4]} isVideo={isVideo(shown[4])} overlay={overlayText} />
        </Box>
      </Box>
    );
  }

  const layout = layouts[count as keyof typeof layouts];
  if (!layout) return null;

  return (
    <Box sx={layout.sx}>
      {shown.map((u, i) => (
        <MediaTile
          key={u}
          url={u}
          isVideo={isVideo(u)}
          sx={layout.tiles[i]?.sx}
        />
      ))}
    </Box>
  );
}

function MediaTile({ url, isVideo, overlay, sx }: { url: string; isVideo: boolean; overlay?: string; sx?: any }) {
  return (
    <Box sx={{ ...styles.tile, ...(sx || {}) }}>
      {isVideo ? (
        <video src={url} muted playsInline style={styles.media as any} />
      ) : (
        <img src={url} alt="" style={styles.media as any} />
      )}
      {isVideo && (
        <Box sx={styles.videoBadge}>
          <PlayArrowIcon sx={styles.videoBadgeIcon} />
        </Box>
      )}
      {overlay && (
        <Box sx={styles.moreOverlay}>
          <Typography sx={styles.moreText}>{overlay}</Typography>
        </Box>
      )}
    </Box>
  );
}

const styles = {
  card: {
    width: "100%"
  },
  header: {
    pb: 0.5
  },
  logoAvatar: {
    width: 40,
    height: 40,
    bgcolor: "#1877F2",
    color: "#fff",
    fontSize: 16
  },
  moreBtn: {
    color: "text.secondary"
  },
  titleWrap: {
    display: "flex",
    flexDirection: "column",
    lineHeight: 1.2,
    minWidth: 0
  },
  pageName: {
    fontWeight: 600,
    fontSize: "0.95rem",
    textTransform: "none",
    letterSpacing: 0
  },
  sublineRow: {
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    mt: 0.25
  },
  sublineText: {
    color: "text.secondary",
    fontSize: "0.8rem"
  },
  dotSeparator: {
    color: "text.secondary",
    fontSize: "0.9rem",
    lineHeight: 1
  },
  sublineIcon: {
    fontSize: 14,
    color: "text.secondary"
  },
  cardContent: {
    padding: "10px 30px 10px!important"
  },
  captionText: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: "0.95rem"
  },
  mediaWrap: {
    px: 2,
    pb: 1
  },
  divider: {
    mx: 2
  },
  actions: {
    px: 1,
    py: 0.5,
    display: "flex",
    gap: 1,
    "& .MuiButton-root": { minWidth: 0 }
  },
  actionBtn: {
    flex: "1 1 0",
    justifyContent: "center",
    textTransform: "none",
    color: "text.secondary",
    gap: 0.75
  },
  actionIcon: {
    fontSize: 22
  },
  tile: {
    position: "relative",
    width: "100%",
    height: "100%",
    overflow: "hidden",
    bgcolor: "grey.100"
  },
  media: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block"
  },
  videoBadge: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 72,
    height: 72,
    borderRadius: "50%",
    bgcolor: "rgba(255,255,255,0.92)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
    zIndex: 2,
    pointerEvents: "none"
  },
  videoBadgeIcon: {
    fontSize: 40,
    color: "rgba(0,0,0,0.85)"
  },
  moreOverlay: {
    position: "absolute",
    inset: 0,
    bgcolor: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 3
  },
  moreText: {
    color: "#fff",
    fontWeight: 700,
    fontSize: "2rem",
    lineHeight: 1
  }
};
